import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { STATUS_CONFIG, CATEGORY_CONFIG, fmtTime, fmtDateTime, fmtDuration } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  MapPin, Navigation, Phone, CheckCircle2, Route, Radio, Clock, ChevronRight, Loader2, Upload, Package, Timer, AlertTriangle, MapPinOff,
} from "lucide-react";

export default function DriverRoutePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [tracking, setTracking] = useState({}); // delivery_id -> token
  const [locStatus, setLocStatus] = useState("idle"); // idle | sharing | blocked | error
  const watchers = useRef({});
  const fileRef = useRef(null);
  const pendingJob = useRef(null);

  const pickCompletion = (jobId) => { pendingJob.current = jobId; fileRef.current?.click(); };

  const onCompletionFile = async (e) => {
    const file = e.target.files?.[0];
    const jobId = pendingJob.current;
    if (e.target) e.target.value = "";
    if (!file || !jobId) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max file size is 5 MB");
    setUploadingId(jobId);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
      await api.post(`/deliveries/${jobId}/documents`, { filename: file.name, mime_type: file.type || "application/octet-stream", data_base64: b64, kind: "completion" });
      toast.success("Completion document uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingId(null); }
  };

  const load = useCallback(() => {
    api.get("/deliveries").then(({ data }) => {
      const active = data.filter((j) => !["completed", "cancelled"].includes(j.status));
      setJobs(active.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    }).catch(() => setJobs([]));
  }, []);

  useEffect(load, [load]);

  // Send live GPS pings for any en_route job with a tracking token
  const startPinging = useCallback((job) => {
    if (!job.tracking_token) return;
    if (!navigator.geolocation) { setLocStatus("error"); return; }
    if (watchers.current[job.id]) return;
    const wid = navigator.geolocation.watchPosition(
      (pos) => {
        setLocStatus("sharing");
        api.post(`/tracking/${job.tracking_token}/ping`, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          speed_kph: pos.coords.speed ? pos.coords.speed * 3.6 : null,
        }).catch(() => {});
      },
      (err) => {
        setLocStatus(err && err.code === 1 ? "blocked" : "error");
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    watchers.current[job.id] = wid;
    setTracking((p) => ({ ...p, [job.id]: job.tracking_token }));
  }, []);

  const enableLocation = useCallback(() => {
    Object.values(watchers.current).forEach((w) => navigator.geolocation?.clearWatch(w));
    watchers.current = {};
    setLocStatus("idle");
    // Prompt for permission then (re)attach watchers to all live jobs.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => { (jobs || []).forEach((j) => { if (j.status === "en_route" && j.tracking_token) startPinging(j); }); },
        (err) => { setLocStatus(err && err.code === 1 ? "blocked" : "error"); },
        { enableHighAccuracy: true, timeout: 20000 }
      );
    } else {
      setLocStatus("error");
    }
  }, [jobs, startPinging]);

  useEffect(() => {
    (jobs || []).forEach((j) => { if (j.status === "en_route" && j.tracking_token) startPinging(j); });
    return () => { Object.values(watchers.current).forEach((w) => navigator.geolocation?.clearWatch(w)); watchers.current = {}; };
  }, [jobs, startPinging]);

  const act = async (job, kind) => {
    setBusyId(job.id);
    try {
      let updated;
      if (kind === "start") {
        ({ data: updated } = await api.post(`/deliveries/${job.id}/start`));
        toast.success("Route started — customer notified");
        startPinging(updated);
      } else if (kind === "checkin") {
        ({ data: updated } = await api.post(`/deliveries/${job.id}/checkin`));
        toast.success("Checked in");
      } else {
        ({ data: updated } = await api.post(`/deliveries/${job.id}/status`, { status: kind }));
        toast.success(`Marked ${STATUS_CONFIG[kind].label}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const hasEnRoute = (jobs || []).some((j) => j.status === "en_route" && j.tracking_token);

  return (
    <div className="mx-auto max-w-3xl">
      <input ref={fileRef} type="file" className="hidden" onChange={onCompletionFile} data-testid="route-completion-input" />
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-stark-terracotta text-white">
          <Route className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-stark-ink sm:text-3xl">My Route</h1>
          <p className="text-sm text-stark-muted">{jobs ? `${jobs.length} stop${jobs.length === 1 ? "" : "s"} today` : "Loading…"}</p>
        </div>
      </div>

      {hasEnRoute && (
        locStatus === "sharing" ? (
          <div className="mt-5 flex items-center gap-3 rounded-2xl border border-stark-success/30 bg-stark-success/10 p-4" data-testid="loc-banner-sharing">
            <Radio className="h-5 w-5 shrink-0 animate-pulse text-stark-success" />
            <div className="text-sm">
              <div className="font-bold text-stark-ink">Sharing your live location</div>
              <div className="text-stark-muted">Customers can see your ETA in real time. Keep this screen open while driving.</div>
            </div>
          </div>
        ) : locStatus === "blocked" ? (
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-stark-error/30 bg-stark-error/10 p-4" data-testid="loc-banner-blocked">
            <MapPinOff className="h-5 w-5 shrink-0 text-stark-error" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-bold text-stark-error">Location is blocked</div>
              <div className="text-stark-muted">Customers can't see your live location. Allow location access for this site in your browser, then tap Enable.</div>
            </div>
            <Button size="sm" className="rounded-xl bg-stark-error font-bold hover:bg-stark-error/90" onClick={enableLocation} data-testid="loc-enable-btn">Enable location</Button>
          </div>
        ) : locStatus === "error" ? (
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-stark-warning/30 bg-stark-warning/10 p-4" data-testid="loc-banner-error">
            <AlertTriangle className="h-5 w-5 shrink-0 text-stark-warning" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-bold text-stark-ink">Couldn't get your location</div>
              <div className="text-stark-muted">Check your GPS/signal and try again so customers can track you.</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-xl font-bold" onClick={enableLocation} data-testid="loc-retry-btn">Retry</Button>
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-3 rounded-2xl border border-stark-border bg-white p-4" data-testid="loc-banner-idle">
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-stark-terracotta" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-bold text-stark-ink">Starting location sharing…</div>
              <div className="text-stark-muted">If your browser asks, tap “Allow” so customers can track your ETA.</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-xl font-bold" onClick={enableLocation} data-testid="loc-enable-idle-btn">Enable</Button>
          </div>
        )
      )}

      <div className="mt-6 space-y-4">
        {!jobs ? (
          [...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)
        ) : jobs.length === 0 ? (
          <div className="stark-card flex flex-col items-center justify-center py-20 text-center">
            <CheckCircle2 className="h-12 w-12 text-stark-success" />
            <p className="mt-3 font-bold text-stark-ink">No assigned route today</p>
            <p className="text-sm text-stark-muted">Enjoy the break — new jobs will appear here.</p>
          </div>
        ) : (
          jobs.map((job, idx) => {
            const cat = CATEGORY_CONFIG[job.category] || { label: job.category };
            const live = tracking[job.id] && locStatus === "sharing";
            return (
              <div key={job.id} className="stark-card stark-fade-up overflow-hidden p-0" style={{ animationDelay: `${idx * 60}ms` }} data-testid={`route-job-${job.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="rounded-md bg-stark-tint px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-stark-terracotta">{cat.label}</span>
                        {live && (
                          <span className="flex items-center gap-1 text-[11px] font-bold uppercase text-stark-success">
                            <Radio className="h-3 w-3 animate-pulse" /> Sharing location
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-stark-ink">{job.customer_name}</h3>
                      <a href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`} target="_blank" rel="noreferrer" className="mt-1 flex items-start gap-1.5 text-sm text-stark-terracotta hover:underline">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {job.address}
                      </a>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-stark-muted">
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDateTime(job.scheduled_at)}</span>
                        {job.category === "delivery" && fmtDuration(job.estimated_duration_min) && (
                          <span className="flex items-center gap-1 font-bold text-stark-terracotta"><Timer className="h-3.5 w-3.5" /> {fmtDuration(job.estimated_duration_min)} on site</span>
                        )}
                        {job.truck_name && <span>{job.truck_name}</span>}
                      </div>
                    </div>
                    <StatusBadge status={job.status} size="sm" />
                  </div>

                  {job.items && job.items.length > 0 && (
                    <div className="mt-3 rounded-xl border border-stark-border p-3" data-testid={`route-items-${job.id}`}>
                      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-stark-muted">
                        <Package className="h-3.5 w-3.5" /> Items to deliver ({job.items.length})
                      </div>
                      <div className="space-y-1.5">
                        {job.items.map((it, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm" data-testid={`route-item-${job.id}-${i}`}>
                            {it.quantity && <span className="mt-0.5 flex h-5 min-w-[1.5rem] shrink-0 items-center justify-center rounded bg-stark-terracotta px-1 text-[11px] font-bold text-white">{it.quantity}</span>}
                            <span className="text-stark-ink"><span className="font-semibold">{it.name}</span>{it.note && <span className="text-stark-muted"> — {it.note}</span>}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {job.notes_for_techs && (
                    <div className="mt-3 rounded-xl bg-stark-tint p-3 text-sm text-stark-ink">
                      <span className="font-semibold text-stark-terracotta">Note: </span>{job.notes_for_techs}
                    </div>
                  )}
                  {job.gated_property && (
                    <div className="mt-2 text-xs font-semibold text-stark-warning">⚠ Gated property — confirm access.</div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    {job.customer_phone && (
                      <a href={`tel:${job.customer_phone}`}>
                        <Button size="sm" variant="outline" className="rounded-xl"><Phone className="mr-1.5 h-4 w-4" /> Call</Button>
                      </a>
                    )}
                    <button onClick={() => navigate(`/jobs/${job.id}`)} className="ml-auto flex items-center gap-1 text-sm font-semibold text-stark-muted hover:text-stark-ink">
                      Details <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-3">
                    <Button size="sm" variant="outline" className="rounded-xl" disabled={uploadingId === job.id} onClick={() => pickCompletion(job.id)} data-testid={`route-upload-completion-${job.id}`}>
                      {uploadingId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1.5 h-4 w-4" /> Upload completion doc</>}
                    </Button>
                  </div>
                </div>

                {/* Sticky-style action bar */}
                <div className="flex gap-2 border-t border-stark-border bg-stark-surface/60 p-3">
                  {job.status === "assigned" && (
                    <>
                      <Button className="flex-1 rounded-xl bg-stark-moss font-bold hover:bg-stark-moss/90" disabled={busyId === job.id} onClick={() => act(job, "checkin")} data-testid={`route-checkin-${job.id}`}>Check In</Button>
                      <Button className="flex-1 rounded-xl bg-stark-terracotta font-bold hover:bg-[#bd4f2c]" disabled={busyId === job.id} onClick={() => act(job, "start")} data-testid={`route-start-${job.id}`}>
                        {busyId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Navigation className="mr-1.5 h-4 w-4" /> Start Route</>}
                      </Button>
                    </>
                  )}
                  {job.status === "checked_in" && (
                    <Button className="flex-1 rounded-xl bg-stark-terracotta font-bold hover:bg-[#bd4f2c]" disabled={busyId === job.id} onClick={() => act(job, "start")} data-testid={`route-start-${job.id}`}>
                      {busyId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Navigation className="mr-1.5 h-4 w-4" /> Start Route</>}
                    </Button>
                  )}
                  {job.status === "en_route" && (
                    <Button className="flex-1 rounded-xl bg-stark-success font-bold hover:bg-stark-success/90" disabled={busyId === job.id} onClick={() => act(job, "arrived")} data-testid={`route-arrived-${job.id}`}>
                      {busyId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Arrived"}
                    </Button>
                  )}
                  {["en_route", "arrived", "delayed"].includes(job.status) && (
                    <Button className="flex-1 rounded-xl bg-stark-ink font-bold text-white hover:bg-stark-ink/90" disabled={busyId === job.id} onClick={() => act(job, "completed")} data-testid={`route-complete-${job.id}`}>
                      <CheckCircle2 className="mr-1.5 h-4 w-4" /> Complete
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
